import { useState } from "react";
import { money } from "@/lib/api";
import { earnings, FEE_DEFAULTS } from "@/lib/earnings";
import { Input } from "@/components/ui/input";
import { Info, SlidersHorizontal } from "lucide-react";

const Line = ({ label, value, strong, minus, testid, hint }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5 text-sm">
    <span className={strong ? "font-semibold text-stone-900" : "text-stone-600"}>{label}{hint && <span className="block text-xs text-stone-400">{hint}</span>}</span>
    <span className={`font-mono-num whitespace-nowrap ${strong ? "font-semibold text-stone-900" : "text-stone-700"}`} data-testid={testid}>{minus ? "−" : ""}{money(value)}</span>
  </div>
);

function YourEarnings({ s, subtotal, tax }) {
  return (
    <div className="rounded-3xl border border-teal-200 bg-gradient-to-br from-teal-50 to-white p-5 sm:p-6" data-testid="earnings-calculator-card">
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">Your Earnings</p>
      <p className="mt-1 font-mono-num text-4xl font-bold text-teal-900 sm:text-5xl" data-testid="earnings-your-earnings">{money(s.hostEarnings)}</p>
      <p className="mt-1 text-sm text-stone-600">after FairStay's 10% commission and paying the lodging tax you collected</p>
      <div className="mt-5 divide-y divide-teal-100 border-t border-teal-100">
        <Line label="Rental and cleaning charges" value={subtotal} testid="earnings-subtotal" />
        <Line label={`Guest lodging tax (${tax.rate}%)`} hint={tax.label} value={s.tax} testid="earnings-tax" />
        <Line label="Total guest payment" value={s.guestTotal} strong testid="earnings-guest-total" />
        <Line label="FairStay commission (10% of rent + cleaning)" value={s.hostFee} minus testid="earnings-commission" />
        <Line label="You receive, including tax collected" value={s.hostReceives} strong testid="earnings-host-receives" />
        <Line label="Lodging tax you file and remit" value={s.tax} minus testid="earnings-tax-remit" />
        <Line label="Your earnings" value={s.hostEarnings} strong testid="earnings-net" />
      </div>
      <p className="mt-4 flex gap-2 rounded-2xl bg-white/80 p-3 text-xs text-stone-600" data-testid="earnings-tax-explainer">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        Lodging tax is added to the guest's bill, not taken from your rent. It passes to you to file and remit (where the law lets owners do this), and FairStay never counts it as commission.
      </p>
    </div>
  );
}

function Assumptions({ fees, setFees }) {
  const [open, setOpen] = useState(false);
  const field = (k, label) => (
    <label key={k} className="text-xs text-stone-600">{label}
      <Input type="number" min="0" max="40" step="0.1" value={fees[k]} onChange={(e) => setFees({ ...fees, [k]: Number(e.target.value) || 0 })} className="mt-1 h-9" data-testid={`fee-assumption-${k}`} />
    </label>
  );
  return (
    <div className="mt-3">
      <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="fee-assumptions-toggle">
        <SlidersHorizontal className="h-3.5 w-3.5" />{open ? "Hide" : "Adjust"} fee assumptions
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-2 gap-3 rounded-2xl border bg-stone-50 p-3 sm:grid-cols-5" data-testid="fee-assumptions-panel">
          {field("airbnbHostOnly", "Airbnb host-only %")}{field("airbnbSplitHost", "Airbnb split: host %")}
          {field("airbnbSplitGuest", "Airbnb split: guest %")}{field("vrboHost", "Vrbo host %")}{field("vrboGuest", "Vrbo traveler %")}
          <button type="button" className="col-span-2 text-left text-xs text-stone-500 underline sm:col-span-5" onClick={() => setFees(FEE_DEFAULTS)} data-testid="fee-assumptions-reset">Reset to typical rates</button>
        </div>
      )}
    </div>
  );
}

const ROWS = [["Total guest price (incl. tax)", "guestTotal"], ["Guest service fee", "guestFee"], ["Homeowner fee", "hostFee"],
  ["Total platform fees", "platformFees"], ["Your earnings", "hostEarnings"]];

function Compare({ rows }) {
  const fs = rows[0];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="earnings-compare">
      {rows.map((r) => {
        const guestSave = r.guestTotal - fs.guestTotal;
        const hostDiff = fs.hostEarnings - r.hostEarnings;
        return (
          <div key={r.key} className={`rounded-2xl border p-4 ${r.key === "fairstay" ? "border-primary bg-teal-50/50 ring-1 ring-primary" : "bg-white"}`} data-testid={`compare-${r.key}`}>
            <p className="font-semibold">{r.name}</p>
            <p className="mb-2 text-xs text-stone-500">{r.note}</p>
            {ROWS.map(([label, k]) => <Line key={k} label={label} value={r[k]} strong={k === "hostEarnings"} testid={`compare-${r.key}-${k}`} />)}
            {r.key !== "fairstay" && (
              <div className="mt-2 space-y-1 border-t pt-2 text-xs" data-testid={`compare-${r.key}-savings`}>
                <p className={guestSave > 0 ? "text-emerald-700" : "text-stone-500"}>{guestSave > 0 ? `Guest saves ${money(guestSave)} on FairStay` : "Same guest price as FairStay"}</p>
                <p className={hostDiff > 0 ? "text-emerald-700" : "text-amber-700"}>{hostDiff > 0 ? `You earn ${money(hostDiff)} more on FairStay` : hostDiff < 0 ? `You'd earn ${money(-hostDiff)} more here` : "Same homeowner earnings"}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Listing-page calculator: what the homeowner earns, with lodging tax kept apart from FairStay's commission.
export function EarningsCalculator({ price, cleaning, tax }) {
  const [nights, setNights] = useState(3);
  const [fees, setFees] = useState(FEE_DEFAULTS);
  const subtotal = Math.round(((price || 0) * nights + (cleaning || 0)) * 100) / 100;
  const rows = earnings(subtotal, tax.rate, fees);
  return (
    <section className="space-y-5" data-testid="earnings-calculator">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-base font-semibold">What you'd earn</p>
        <label className="flex items-center gap-2 text-sm text-stone-600">Stay length
          <select value={nights} onChange={(e) => setNights(Number(e.target.value))} className="rounded-xl border bg-white px-3 py-1.5 text-sm" data-testid="earnings-nights-select">
            {[1, 2, 3, 5, 7, 14, 30].map((n) => <option key={n} value={n}>{n} night{n > 1 ? "s" : ""}</option>)}
          </select>
        </label>
      </div>
      <YourEarnings s={rows[0]} subtotal={subtotal} tax={tax} />
      <div>
        <p className="mb-2 text-sm font-semibold">Same stay on other platforms (estimates)</p>
        <Compare rows={rows} />
        <Assumptions fees={fees} setFees={setFees} />
        <p className="mt-2 text-xs text-stone-500" data-testid="earnings-disclaimer">
          Estimates use typical published fees as of Nov 2026: Airbnb's 15.5% host-only fee (most hosts) or its older split fee (3% host plus about 14.1% guest), and Vrbo's 12% commission plus a traveler fee that varies (about 10% used here). Real fees vary by account, location and pricing. Hosts on host-only plans often raise nightly prices to cover the fee. FairStay won't always earn you more. The same lodging tax is applied on every platform for comparison.
        </p>
      </div>
    </section>
  );
}
